import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { extractErrorMessage } from '../../utils/errorHandler';
import axios from 'axios';
import API_CONFIG from '../../config/apiConfig';

// Configure axios for this component
axios.defaults.withCredentials = false; // Don't send cookies
axios.defaults.headers.common['Accept'] = 'application/json';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleSubmit = async (event?: React.FormEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
      const loginUrl = `${authServiceUrl}/api/v1/auth/login`;
      
      const response = await axios.post(loginUrl, {
        email: formData.email,
        password: formData.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });
      
      // Extract from the correct response structure
      const { access_token, refresh_token, user: authUser } = response.data.data;
      
      // Debug logging to track the login redirect issue
      console.log('[LoginPage] Full response.data:', response.data);
      console.log('[LoginPage] Auth user from response:', authUser);
      console.log('[LoginPage] Access token present:', !!access_token);
      console.log('[LoginPage] Refresh token present:', !!refresh_token);
      console.log('[LoginPage] Auth user keys:', Object.keys(authUser || {}));
      console.log('[LoginPage] organizationId in authUser:', authUser?.organizationId);
      console.log('[LoginPage] organization_id in authUser:', authUser?.organization_id);
      
      // Store refresh token if provided
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
        console.log('[LoginPage] Stored refresh token');
      }
      
      // Also decode the token to see what's in it
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1]));
        console.log('[LoginPage] Token payload:', payload);
        console.log('[LoginPage] organizationId in token:', payload.organizationId);
        console.log('[LoginPage] organization_id in token:', payload.organization_id);
      } catch (e) {
        console.error('[LoginPage] Failed to decode token:', e);
      }
      
      // Extract additional fields from JWT token if missing from user object
      let tokenPayload = null;
      try {
        tokenPayload = JSON.parse(atob(access_token.split('.')[1]));
      } catch (e) {
        console.error('[LoginPage] Failed to decode token for fallback:', e);
      }
      
      // Transform the auth service user to match our User interface
      // Use JWT token as fallback for missing fields
      const user = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.full_name || authUser.name,
        firstName: authUser.full_name?.split(' ')[0] || '',
        lastName: authUser.full_name?.split(' ')[1] || '',
        organizationId: authUser.organizationId || authUser.organization_id || tokenPayload?.organizationId,
        organizationName: authUser.organizationName || authUser.organization_name || tokenPayload?.organizationName,
        organizationRole: authUser.organizationRole || authUser.organization_role || tokenPayload?.organizationRole,
        role: authUser.role || 'user',
      };
      
      console.log('[LoginPage] Constructed user object:', user);
      console.log('[LoginPage] User organizationId:', user.organizationId);
      console.log('[LoginPage] User organizationRole:', user.organizationRole);
      console.log('[LoginPage] organizationId source:', 
        authUser.organizationId ? 'authUser.organizationId' :
        authUser.organization_id ? 'authUser.organization_id' :
        tokenPayload?.organizationId ? 'tokenPayload.organizationId' : 'not found');
      
      // Clear any stale auth state before setting new one
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      // Store refresh token if provided (before calling setAuth)
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      
      // Set auth data (login function from authStore accepts refresh token)
      const { login } = useAuthStore.getState();
      login(user, access_token, refresh_token);
      
      // Navigate to chat page
      navigate('/chat');
    } catch (error: any) {
      
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <div
        style={{
          marginTop: 64,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          style={{
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: '#fff',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            style={{
              marginBottom: 8,
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #325BA8 30%, #4DC4D2 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Prismatic Forge
          </Typography>
          
          <Typography variant="h6" style={{ marginBottom: 24 }}>
            Sign in to your account
          </Typography>

          {error && (
            <Alert severity="error" style={{ width: '100%', marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <form noValidate style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange('email')}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange('password')}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              style={{ marginTop: 24, marginBottom: 16, paddingTop: 12, paddingBottom: 12 }}
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>

            <Divider style={{ marginTop: 24, marginBottom: 24 }}>OR</Divider>

            <div style={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
                Don't have an account?
              </Typography>
              <Link
                component={RouterLink}
                to="/signup"
                variant="body1"
                style={{ fontWeight: 500 }}
              >
                Sign up for Prismatic Forge
              </Link>
            </div>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                color="text.secondary"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </Paper>
      </div>
    </Container>
  );
};

export default LoginPage;